import React from 'react';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import {
  FDBox,
  FDContentContainer,
  FDEmphasisSurface,
  FDFacetLogo,
  FDIcon,
  FDImage,
  FDLozenge,
  FDPaper,
  FDPrimaryActionButton,
  FDSecondaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import useScrollTopOnMount from 'Hooks/useScrollTopOnMount';
import useEventTrackingLogEvent, { categories, eventSubTypes, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { dataAttrs } from 'Helpers/Testing';
import {
  useBuildCommonFQEventProperties,
} from 'Views/FreemiumEntry/HasRelationship/Content/Components/utils';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import fr from 'facet-rest';
import sentry from 'Integrations/Sentry';
import { useSnackbarContext } from 'Context/SnackbarContext';
import useMobileLayout from 'Hooks/useMobileLayout';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { UpgradeOffer } from './UpgradeOffer/UpgradeOffer';
import { UpgradeOfferSecondary } from './UpgradeOffer/UpgradeOfferSecondary';

const IMAGE_MARGIN = 120;
const IMAGE_OFFSET = IMAGE_MARGIN / 2;

const useStyles = buildUseStyles('InsightUpgrade', (theme) => ({
  heroImage: {
    paddingTop: theme.spacing(4),
    marginBottom: `-${IMAGE_MARGIN}px`,
  },
}));

const InsightUpgrade = () => {
  const classes = useStyles();
  const isMobile = useMobileLayout();
  const { displayErrorMessage } = useSnackbarContext();
  const [loading, setLoading] = React.useState(false);
  const { resource, onSubmit, saving } = useMetaLayoutContext();
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const { buildCommonFQEventProperties } = useBuildCommonFQEventProperties();

  useScrollTopOnMount();

  const onClickCancel = async (rel) => {
    try {
      setLoading(true);
      logStandardizedEvent(categories.facetQuiz, eventTypes.insight, eventSubTypes.clickedCTA, {
        eventProperties: {
          ...buildCommonFQEventProperties(resource),
          insightId: resource.attr('insightId'),
          insightReferenceName: resource.attr('insightReferenceName'),
          action: 'request-cancel',
        },
      });
      await rel.link('related').post();
      onSubmit();
    } catch (err) {
      setLoading(false);
      sentry.createSentryErrorLog(err, err?.status);
      displayErrorMessage(err, 'Failed to request cancellation');
    }
  };

  const onClickUpgrade = async (rel) => {
    setLoading(true);
    try {
      const [requestProductChangeRes] = await rel.link('related').post();

      const redirectRel = requestProductChangeRes.relationship(fr.relationships.redirect.name);
      if (!redirectRel) {
        return;
      }

      // In most cases we should not do this to navigate, but this is special because in this component we are
      // doing a handoff from the marketing app (which lives at a different domain) into the main app. Usually
      // we should either use load a collection into page context (or use history.push() if we are not working within
      // page context) to navigate around the app.
      window.location.href = redirectRel.link('related').url();
    } catch (err) {
      setLoading(false);
      sentry.createSentryErrorLog(err, err?.status);
      displayErrorMessage(err, 'Failed to request product change');
    }
  };

  useBoneBackgroundColor();

  const buttonsLoading = saving || loading;

  return (
    <>
      <FDStack direction={'column'} spacing={5} {...dataAttrs('insight-container')}>
        <FDEmphasisSurface rounded={false}>
          <FDContentContainer maxWidth={'sm'}>
            <FDStack direction={'column'} spacing={4}>
              <FDStack alignItems={'center'} justifyContent={'center'} paddingY={3}>
                <FDFacetLogo />
              </FDStack>
              <FDStack direction={'column'} spacing={2}>
                <MetaControlField attr={'miniHeader'} />
                <MetaControlField attr={'title'} />
                <MetaControlField attr={'description'} />
                {resource.withRelationship(fr.relationships.upgradeNow.name, (rel) => (
                  <FDStack>
                    <FDPrimaryActionButton
                      loading={buttonsLoading}
                      onClick={() => {
                        logStandardizedEvent(categories.facetQuiz, eventTypes.insight, eventSubTypes.clickedCTA, {
                          eventProperties: {
                            ...buildCommonFQEventProperties(resource),
                            insightId: resource.attr('insightId'),
                            insightReferenceName: resource.attr('insightReferenceName'),
                            action: 'upgrade-now',
                            isSecondary: false,
                          },
                        });
                        onClickUpgrade(rel);
                      }}
                      label={rel.meta('prompt')}
                    />
                  </FDStack>
                ), () => null)}
              </FDStack>
              {resource.withRelationship(fr.relationships.image.name, (rel) => (
                <div className={classes.heroImage}>
                  <FDImage
                    src={rel.link('related').url()}
                    alt={rel.meta('altText') || ''}
                    aspectRatio={rel.meta('aspectRatio')}
                    rounded={false}
                    maxWidth={'100%'}
                  />
                </div>
              ), () => null)}
            </FDStack>
          </FDContentContainer>
        </FDEmphasisSurface>
        <FDBox height={`${IMAGE_OFFSET}px`} />
        <FDContentContainer maxWidth={'sm'}>
          <FDStack direction={'column'} spacing={4}>
            <FDStack direction={'column'} alignItems={'center'} justifyContent={'center'}>
              <FDLozenge color={'warning'} label={resource.attr('offerExpiration')} size={'lg'} />
            </FDStack>
            {resource.attr('primaryOffer') ? resource.attr('primaryOffer').resources().list().map((item) => (
              <UpgradeOffer
                resource={item}
                loading={buttonsLoading}
                onClickCancel={onClickCancel}
                onClickUpgrade={onClickUpgrade}
                key={item.id()}
              />
            )) : null}
            {(resource.attr('secondaryOffer')) ? resource.attr('secondaryOffer').resources().list().map((item) => (
              <FDPaper appearance={'elevated'} spacing={3} padding={3}>
                <UpgradeOfferSecondary
                  resource={item}
                  loading={buttonsLoading}
                  onClickUpgrade={onClickUpgrade}
                  key={item.id()}
                />
              </FDPaper>
            )) : null}
            {resource.attr('renewalText') && (
              <FDPaper padding={3} appearance={'flat'}>
                <FDStack direction={'column'} spacing={2} paddingY={2} alignItems={'center'}>
                  <FDIcon icon={resource.attr('renewalIcon')} size={'lg'} />
                  <MetaControlField attr={'renewalText'} />
                  {resource.withRelationship(fr.relationships.planToCancel.name, (rel) => (
                    <FDSecondaryActionButton
                      label={rel.meta('prompt')}
                      onClick={() => onClickCancel(rel)}
                      loading={buttonsLoading}
                    />
                  ), () => null)}
                </FDStack>
              </FDPaper>)}
          </FDStack>
        </FDContentContainer>
        <FDEmphasisSurface rounded={false}>
          <FDContentContainer maxWidth={'sm'}>
            <FDStack direction={'column'} spacing={2} paddingY={4}>
              <MetaControlField attr={'andersTitle'} />
              <MetaControlField attr={'andersDescription'} />
              <FDStack spacing={2}>
                {resource.withRelationship(fr.relationships.andersSignature.name, (rel) => (
                  <FDImage
                    src={rel.link('related').url()}
                    alt={rel.meta('altText') || ''}
                    aspectRatio={rel.meta('aspectRatio')}
                    maxWidth={160}
                  />
                ), () => null)}
                {resource.withRelationship(fr.relationships.andersImage.name, (rel) => (
                  <FDImage
                    src={rel.link('related').url()}
                    alt={rel.meta('altText') || ''}
                    aspectRatio={rel.meta('aspectRatio')}
                    maxWidth={100}
                  />
                ), () => null)}
              </FDStack>
              <MetaControlField attr={'andersName'} />
              {resource.withRelationship(fr.relationships.claimMyUpgrade.name, (rel) => (
                <FDStack>
                  <FDPrimaryActionButton
                    loading={buttonsLoading}
                    onClick={() => {
                      logStandardizedEvent(categories.facetQuiz, eventTypes.insight, eventSubTypes.clickedCTA, {
                        eventProperties: {
                          ...buildCommonFQEventProperties(resource),
                          insightId: resource.attr('insightId'),
                          insightReferenceName: resource.attr('insightReferenceName'),
                          action: 'claim-my-upgrade',
                          isSecondary: false,
                        },
                      });
                      onClickUpgrade(rel);
                    }}
                    label={rel.meta('prompt')}
                    isFullWidth={isMobile}
                  />
                </FDStack>
              ), () => null)}
            </FDStack>
          </FDContentContainer>
        </FDEmphasisSurface>
      </FDStack>
      <FDStack padding={3}>
        <MetaControlField attr={'disclosure'} />
      </FDStack>
    </>
  );
};

export { InsightUpgrade };
